import React, { useRef, useState } from 'react';
import jsPDF from 'jspdf';
import s3Service from '../services/s3Service.js';

// NDA Document Text Constants
const NDA_CONSTANTS = {
  HEADER: {
    TITLE: 'MUTUAL NON-DISCLOSURE AGREEMENT',
    MODAL_TITLE: 'Mutual Non-Disclosure Agreement',
    STEP_LABEL: 'Review and Agree to Terms'
  },
  SECTIONS: {
    PURPOSE: {
      TITLE: '1. Purpose',
      CONTENT: 'The parties wish to explore potential business opportunities and may need to share confidential and proprietary information. This Agreement establishes the terms and conditions under which such information will be protected.'
    },
    DEFINITION: {
      TITLE: '2. Definition of Confidential Information',
      CONTENT: '"Confidential Information" means all non-public, proprietary, or confidential information disclosed by either party to the other, whether orally, in writing, or in any other form, including but not limited to:',
      ITEMS: [
        'Business plans, strategies, and financial information',
        'Technical data, know-how, and trade secrets',
        'Customer lists, supplier information, and market research',
        'Software, algorithms, and proprietary methodologies',
        'Any other information that is marked or designated as confidential'
      ]
    },
    OBLIGATIONS: {
      TITLE: '3. Obligations of Receiving Party',
      CONTENT: 'The receiving party agrees to:',
      ITEMS: [
        'Hold all Confidential Information in strict confidence',
        'Use Confidential Information solely for the purpose of evaluating potential business opportunities',
        'Not disclose Confidential Information to any third party without prior written consent',
        'Take reasonable precautions to protect the confidentiality of the information',
        'Return or destroy all Confidential Information upon request'
      ]
    },
    EXCEPTIONS: {
      TITLE: '4. Exceptions',
      CONTENT: 'The obligations of confidentiality shall not apply to information that:',
      ITEMS: [
        'Is or becomes publicly available through no breach of this Agreement',
        'Was known to the receiving party prior to disclosure',
        'Is independently developed by the receiving party',
        'Is rightfully received from a third party without breach of confidentiality',
        'Is required to be disclosed by law or court order'
      ]
    },
    TERM_AND_TERMINATION: {
      TITLE: '5. Term and Termination',
      CONTENT: 'This Agreement shall remain in effect for a period of one (1) year from the Effective Date, unless terminated earlier by either party with thirty (30) days written notice or by mutual written consent. The obligations of confidentiality shall survive termination of this Agreement for a period of one (1) year after the effective date of termination.'
    },
    NO_LICENSE: {
      TITLE: '6. No License or Ownership',
      CONTENT: 'Nothing in this Agreement shall be construed as granting any license or ownership rights to any Confidential Information. All Confidential Information remains the property of the disclosing party.'
    },
    REMEDIES: {
      TITLE: '7. Remedies',
      CONTENT: 'The parties acknowledge that any breach of this Agreement may cause irreparable harm for which monetary damages may be inadequate. Therefore, the non-breaching party shall be entitled to seek injunctive relief in addition to any other remedies available at law or in equity.'
    },
    GOVERNING_LAW: {
      TITLE: '8. Governing Law',
      CONTENT: 'This Agreement shall be governed by and construed in accordance with the laws of [State/Country], without regard to conflict of law principles.'
    },
    ENTIRE_AGREEMENT: {
      TITLE: '9. Entire Agreement',
      CONTENT: 'This Agreement constitutes the entire agreement between the parties regarding the subject matter hereof and supersedes all prior negotiations, representations, or agreements relating thereto.'
    },
    AGREEMENT: {
      TITLE: '10. Agreement',
      ACKNOWLEDGMENT: 'I acknowledge that I have read, understood, and agree to be bound by the terms and conditions of this Mutual Non-Disclosure Agreement.',
      AUTHORITY_CONFIRMATION: 'By signing below, I confirm that I have the authority to enter into this agreement on behalf of'
    },
    SIGNATURES: {
      TITLE: '11. Signatures',
      COMPANY_NAME: 'HalalCred',
      LABELS: {
        DATE: 'Date:'
      }
    }
  },
  CONTACT: {
    TITLE: 'Contact Information',
    INTRO: 'For questions about this agreement, please contact:',
    COMPANY_NAME: 'HalalCred',
    EMAIL: 'salaam@halalcred.com',
    PHONE: '+1-425-340-0980'
  }
};

const NDAModal = ({ isOpen, onClose, partnerName = "[Partner Name]", onStepComplete, existingDocument = null }) => {
  if (!isOpen) return null;

  // PDF generation setup
  const targetRef = useRef();
  const [isAgreed, setIsAgreed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  // Template-based PDF generation using jsPDF text rendering
  const generatePDFFromTemplate = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    let yPosition = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Helper function to add text with word wrap
    const addText = (text, fontSize = 11, isBold = false, align = 'left') => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      
      const maxWidth = pageWidth - (margin * 2);
      const lines = pdf.splitTextToSize(text, maxWidth);
      const singleLineHeight = fontSize * 0.35; // Approximate height per line
      
      lines.forEach((line) => {
        // Check if we need a new page before adding this line
        if (yPosition + singleLineHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.text(line, margin, yPosition, { align });
        yPosition += singleLineHeight;
      });
    };

    // Helper function to add a section
    const addSection = (title, content, listItems = null) => {
      // Section title
      addText(title, 14, true);
      yPosition += 2;
      
      // Section content
      if (content) {
        addText(content, 11, false);
        yPosition += 2;
      }
      
      // List items if provided
      if (listItems && Array.isArray(listItems)) {
        listItems.forEach((item) => {
          addText(`• ${item}`, 11, false);
        });
        yPosition += 2;
      }
      
      yPosition += 4; // Space after section
    };

    // Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(NDA_CONSTANTS.HEADER.TITLE, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Effective Date: ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text(`Parties: ${NDA_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME} and ${partnerName}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Draw a line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    // Section 1
    addSection(
      NDA_CONSTANTS.SECTIONS.PURPOSE.TITLE,
      NDA_CONSTANTS.SECTIONS.PURPOSE.CONTENT
    );

    // Section 2
    addSection(
      NDA_CONSTANTS.SECTIONS.DEFINITION.TITLE,
      NDA_CONSTANTS.SECTIONS.DEFINITION.CONTENT,
      NDA_CONSTANTS.SECTIONS.DEFINITION.ITEMS
    );

    // Section 3
    addSection(
      NDA_CONSTANTS.SECTIONS.OBLIGATIONS.TITLE,
      NDA_CONSTANTS.SECTIONS.OBLIGATIONS.CONTENT,
      NDA_CONSTANTS.SECTIONS.OBLIGATIONS.ITEMS
    );

    // Section 4
    addSection(
      NDA_CONSTANTS.SECTIONS.EXCEPTIONS.TITLE,
      NDA_CONSTANTS.SECTIONS.EXCEPTIONS.CONTENT,
      NDA_CONSTANTS.SECTIONS.EXCEPTIONS.ITEMS
    );

    // Section 5
    addSection(
      NDA_CONSTANTS.SECTIONS.TERM_AND_TERMINATION.TITLE,
      NDA_CONSTANTS.SECTIONS.TERM_AND_TERMINATION.CONTENT
    );

    // Section 6
    addSection(
      NDA_CONSTANTS.SECTIONS.NO_LICENSE.TITLE,
      NDA_CONSTANTS.SECTIONS.NO_LICENSE.CONTENT
    );

    // Section 7
    addSection(
      NDA_CONSTANTS.SECTIONS.REMEDIES.TITLE,
      NDA_CONSTANTS.SECTIONS.REMEDIES.CONTENT
    );

    // Section 8
    addSection(
      NDA_CONSTANTS.SECTIONS.GOVERNING_LAW.TITLE,
      NDA_CONSTANTS.SECTIONS.GOVERNING_LAW.CONTENT
    );

    // Section 9
    addSection(
      NDA_CONSTANTS.SECTIONS.ENTIRE_AGREEMENT.TITLE,
      NDA_CONSTANTS.SECTIONS.ENTIRE_AGREEMENT.CONTENT
    );

    // Section 10
    checkPageBreak(20);
    addSection(
      NDA_CONSTANTS.SECTIONS.AGREEMENT.TITLE,
      NDA_CONSTANTS.SECTIONS.AGREEMENT.ACKNOWLEDGMENT,
      null
    );
    addText(`${NDA_CONSTANTS.SECTIONS.AGREEMENT.AUTHORITY_CONFIRMATION} ${partnerName} and that all information provided is accurate and complete.`, 10, false);
    yPosition += 2;
    addText(`Status: ${isAgreed ? 'AGREED' : 'NOT AGREED'}`, 11, true);
    yPosition += 8;

    // Section 11 - Signatures
    checkPageBreak(60);
    addText(NDA_CONSTANTS.SECTIONS.SIGNATURES.TITLE, 14, true);
    yPosition += 8;

    // Signature boxes
    const sigBoxWidth = (pageWidth - (margin * 2) - 10) / 2;
    const sigBoxY = yPosition;

    // Left signature box (HalalCred)
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(NDA_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME, margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(NDA_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE, margin, yPosition);
    yPosition += 4;
    pdf.text(currentDate, margin, yPosition);
    yPosition += 8;

    // Right signature box (Partner)
    yPosition = sigBoxY;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const partnerNameLines = pdf.splitTextToSize(partnerName, sigBoxWidth);
    pdf.text(partnerNameLines, margin + sigBoxWidth + 10, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(NDA_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE, margin + sigBoxWidth + 10, yPosition);
    yPosition += 4;
    pdf.text(currentDate, margin + sigBoxWidth + 10, yPosition);
    yPosition += 12;

    // Contact Information
    checkPageBreak(30);
    addText(NDA_CONSTANTS.CONTACT.TITLE, 12, true);
    yPosition += 4;
    addText(NDA_CONSTANTS.CONTACT.INTRO, 11, false);
    yPosition += 2;
    addText(NDA_CONSTANTS.CONTACT.COMPANY_NAME, 11, true);
    addText(`Email: ${NDA_CONSTANTS.CONTACT.EMAIL}`, 11, false);
    addText(`Phone: ${NDA_CONSTANTS.CONTACT.PHONE}`, 11, false);

    return pdf;
  };

  // Handle PDF generation using template approach
  const handleGeneratePDF = async () => {
    if (!isAgreed) {
      alert('Please agree to the terms and conditions before generating the PDF.');
      return;
    }

    try {
      // Show loading state
      const button = document.querySelector('[data-pdf-button]');
      if (button) {
        button.disabled = true;
        button.textContent = 'Generating PDF...';
      }

      // Generate PDF from template
      const pdf = generatePDFFromTemplate();
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `NDA_${partnerName.replace(/\s+/g, '_')}_${timestamp}.pdf`;
      
      // Download the PDF locally
      pdf.save(filename);
      
      // Upload to S3
      setIsUploading(true);
      setUploadStatus('Uploading PDF to secure storage...');
      
      try {
        // Convert PDF to blob for S3 upload
        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
        
        // Get partner ID for S3 upload
        const userId = localStorage.getItem('userId') || 'demo-user-id';
        
        // Upload to S3
        const uploadResult = await s3Service.uploadFile(pdfFile, userId, 'nda');
        
        setUploadStatus('PDF uploaded successfully to secure storage');
        
        // Complete the step and move to next
        if (onStepComplete) {
          onStepComplete({
            step: 'nda_agreement',
            completed: true,
            timestamp: new Date().toISOString(),
            filename: filename,
            partnerName: partnerName,
            s3Key: uploadResult.s3Key,
            s3Url: uploadResult.s3Url,
            documentPath: uploadResult.s3Key // Use S3 key as document path
          });
        }
        
        // Show success message with better UX
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        successMessage.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
          <span>PDF generated and uploaded successfully! Step completed.</span>
        `;
        document.body.appendChild(successMessage);
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 4000);
        
        // Close the modal after successful completion
        setTimeout(() => {
          onClose();
        }, 1000);
        
      } catch (uploadError) {
        console.error('S3 upload failed:', uploadError);
        console.log('NDA Modal - S3 upload failed, NOT completing step to preserve document');
        setUploadStatus('Upload failed - document saved locally only');
        
        // DO NOT complete the step if upload fails - we need the document saved!
        // The user can retry or contact support
        
        // Show error message with retry option
        const errorMessage = document.createElement('div');
        errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        errorMessage.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
          </svg>
          <span>Upload failed! PDF saved locally. Please retry or contact support.</span>
        `;
        document.body.appendChild(errorMessage);
        setTimeout(() => {
          if (document.body.contains(errorMessage)) {
            document.body.removeChild(errorMessage);
          }
        }, 8000);
      } finally {
        setIsUploading(false);
        setUploadStatus('');
        
        // Reset button state
        if (button) {
          button.disabled = false;
          button.textContent = 'Generate PDF';
        }
        
        // Only close the modal if upload was successful (success case is handled above)
        // If upload failed, keep modal open so user can retry
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      // Show error message with better UX
      const errorMessage = document.createElement('div');
      errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      errorMessage.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
        </svg>
        <span>Failed to generate PDF. Please try again.</span>
      `;
      document.body.appendChild(errorMessage);
      setTimeout(() => {
        document.body.removeChild(errorMessage);
      }, 5000);
      
      // Reset button state
      const button = document.querySelector('[data-pdf-button]');
      if (button) {
        button.disabled = false;
        button.textContent = 'Generate PDF';
      }
    }
  };

  // Generate current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{NDA_CONSTANTS.HEADER.MODAL_TITLE}</h2>
            <p className="text-sm text-gray-600 mt-1">{NDA_CONSTANTS.HEADER.STEP_LABEL}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="max-w-none" ref={targetRef}>
            {/* Document Info */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Effective Date:</strong> {currentDate}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Parties:</strong> {NDA_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME} and {partnerName}
              </p>
            </div>

            {/* Existing Document Section */}
            {existingDocument && (
              <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-3">📄 Completed NDA Document</h3>
                <div className="space-y-2">
                  <p className="text-sm text-green-700">
                    <strong>Document:</strong> {existingDocument.filename || 'NDA Agreement'}
                  </p>
                  <p className="text-sm text-green-700">
                    <strong>Completed:</strong> {existingDocument.completedAt ? new Date(existingDocument.completedAt).toLocaleDateString() : 'Unknown'}
                  </p>
                  {existingDocument.s3Key && (
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={async () => {
                          try {
                            const signedUrl = await s3Service.getSignedUrl(existingDocument.s3Key);
                            window.open(signedUrl, '_blank');
                          } catch (error) {
                            console.error('Error viewing document:', error);
                            alert('Failed to open document. Please try again.');
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Document
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const signedUrl = await s3Service.getSignedUrl(existingDocument.s3Key);
                            const link = document.createElement('a');
                            link.href = signedUrl;
                            link.download = existingDocument.filename || 'NDA_Agreement.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          } catch (error) {
                            console.error('Error downloading document:', error);
                            alert('Failed to download document. Please try again.');
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Document
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* NDA Content - Hide when completed */}
            {!existingDocument && (
              <>
            <section className="mb-10 p-6 bg-white border-l-4 border-blue-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.PURPOSE.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {NDA_CONSTANTS.SECTIONS.PURPOSE.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-green-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.DEFINITION.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base mb-4">
                {NDA_CONSTANTS.SECTIONS.DEFINITION.CONTENT}
              </p>
              <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base">
                {NDA_CONSTANTS.SECTIONS.DEFINITION.ITEMS.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-purple-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.OBLIGATIONS.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base mb-4">
                {NDA_CONSTANTS.SECTIONS.OBLIGATIONS.CONTENT}
              </p>
              <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base">
                {NDA_CONSTANTS.SECTIONS.OBLIGATIONS.ITEMS.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-orange-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.EXCEPTIONS.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base mb-4">
                {NDA_CONSTANTS.SECTIONS.EXCEPTIONS.CONTENT}
              </p>
              <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base">
                {NDA_CONSTANTS.SECTIONS.EXCEPTIONS.ITEMS.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10 p-6 bg-yellow-50 border-l-4 border-yellow-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.TERM_AND_TERMINATION.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {NDA_CONSTANTS.SECTIONS.TERM_AND_TERMINATION.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-indigo-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.NO_LICENSE.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {NDA_CONSTANTS.SECTIONS.NO_LICENSE.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-red-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.REMEDIES.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {NDA_CONSTANTS.SECTIONS.REMEDIES.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-teal-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.GOVERNING_LAW.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {NDA_CONSTANTS.SECTIONS.GOVERNING_LAW.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-pink-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.ENTIRE_AGREEMENT.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {NDA_CONSTANTS.SECTIONS.ENTIRE_AGREEMENT.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-green-50 border-l-4 border-green-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.AGREEMENT.TITLE}</h2>
              <div className="mb-6 p-4 bg-white rounded-lg border shadow-sm">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="nda-agreement"
                    checked={isAgreed}
                    onChange={(e) => setIsAgreed(e.target.checked)}
                    className="mt-1 h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="nda-agreement" className="text-gray-800 leading-relaxed text-base cursor-pointer">
                    <strong className="text-green-800">{NDA_CONSTANTS.SECTIONS.AGREEMENT.ACKNOWLEDGMENT}</strong> 
                    <br /><br />
                    <span className="text-sm text-gray-600">
                      {NDA_CONSTANTS.SECTIONS.AGREEMENT.AUTHORITY_CONFIRMATION} <strong>{partnerName}</strong> and that all information provided is accurate and complete.
                    </span>
                  </label>
                </div>
                {isAgreed && (
                  <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-sm text-green-800 flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Great! You can now generate and complete the NDA document.</span>
                  </div>
                )}
              </div>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-gray-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{NDA_CONSTANTS.SECTIONS.SIGNATURES.TITLE}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900 mb-2">{NDA_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME}</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {NDA_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE.replace(':', '')}
                      </label>
                      <div className="h-8 border-b border-gray-300 text-gray-600 text-sm flex items-center">
                        {currentDate}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900 mb-2">{partnerName}</p>
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isAgreed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                      }`}>
                        {isAgreed && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">
                        {isAgreed ? 'Agreed to Terms' : 'Not Agreed'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {NDA_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE.replace(':', '')}
                      </label>
                      <div className="h-8 border-b border-gray-300 text-gray-600 text-sm flex items-center">
                        {currentDate}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
              </>
            )}

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{NDA_CONSTANTS.CONTACT.TITLE}</h3>
              <p className="text-gray-700 mb-2">
                {NDA_CONSTANTS.CONTACT.INTRO}
              </p>
              <p className="text-gray-700">
                <strong>{NDA_CONSTANTS.CONTACT.COMPANY_NAME}</strong><br />
                Email: {NDA_CONSTANTS.CONTACT.EMAIL}<br />
                Phone: {NDA_CONSTANTS.CONTACT.PHONE}
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4">
          {uploadStatus && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                {isUploading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
                <span className="text-sm text-blue-700 font-medium">{uploadStatus}</span>
              </div>
              {isUploading && (
                <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-progress-pulse" style={{width: '60%'}}></div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
            {!existingDocument && (
              <button
                onClick={handleGeneratePDF}
                data-pdf-button
                disabled={!isAgreed || isUploading}
                className={`px-4 py-2 rounded-md transition-colors ${
                  isAgreed && !isUploading
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isUploading 
                  ? 'Uploading to S3...' 
                  : isAgreed 
                    ? 'Complete Step & Generate PDF' 
                    : 'Agree to Terms First'
                }
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NDAModal;